import { ADDRESS_MODES } from '@/constants';
import { buildPayload } from '@/helpers/user';
import {
  addUpdateUserAddress,
  getAllUserAddress,
} from '@/services/user/addresses';
import { getRegionIDWithLat } from '@/utils/global';
import { event_add_shipping_address, event_update_address } from '@/utils/gtm';
import { getCookie } from 'cookies-next';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/utils/toast';
import useSWRMutation from 'swr/mutation';
import AddEditAddressManual from './add-edit-address-types/add-edit-address-manual';
import AddEditAddressMap from './add-edit-address-types/add-edit-address-map';
import AddEditAddressNationalAddress from './add-edit-address-types/add-edit-address-national-address';

const AddEditAddressModal = ({
  addressType,
  selectedAddress,
  setShippingAddress,
  handleOnClose,
  refetchAddresses,
}) => {
  const { data: session } = useSession();
  const t = useTranslations('');
  const mage = getCookie('mage');
  const [isLoading, setIsLoading] = useState(false);

  // `city` is the stored address's `region_code`: the city in English
  // ("Riyadh") on both stores and in every address mode. The save response only
  // echoes the request, where it is empty, so the customer's address list is
  // read back once. Not awaited, so the modal is not held up.
  const reportAddedAddress = async (savedAddress, submittedValues) => {
    const list = await getAllUserAddress(mage);
    const stored = list?.data?.data?.items?.find(
      (address) => address?.id === savedAddress?.id
    );
    event_add_shipping_address({
      user_id: session?.user?.id,
      city:
        stored?.region?.region_code ||
        savedAddress?.region?.region ||
        submittedValues?.region,
      district: savedAddress?.city || submittedValues?.city,
      address: submittedValues?.street,
    });
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    let submittedValues = values;

    if (
      addressType == ADDRESS_MODES.AUTO ||
      addressType == ADDRESS_MODES.NATIONAL
    ) {
      const data = await getRegionIDWithLat(
        submittedValues?.lat,
        submittedValues?.long
      );

      submittedValues = {
        ...submittedValues,
        city_id: data?.city_id,
        region_id: data?.region_id,
        city: data?.city_name,
      };
    }

    try {
      const payload = buildPayload({
        submittedValues,
        selectedAddress,
        user: session?.user,
        addressType,
      });
      const response = await submitAddress(payload);
      if (response?.error?.error?.message) {
        toast.error(response.error.error.message);
        return;
      }

      if (!selectedAddress?.id) {
        reportAddedAddress(response?.data?.data, submittedValues);
      }

      event_update_address({
        district: submittedValues?.city,
        region: submittedValues?.region || submittedValues?.city,
        street: submittedValues?.street,
      });

      toast.success(
        selectedAddress?.id
          ? t('user.update_success_key')
          : t('user.address_create_success_key')
      );

      setShippingAddress(response?.data?.data);
      handleOnClose();
      if (refetchAddresses) {
        refetchAddresses();
      }
    } catch (error) {
      console.error('Address submission error:', error);
      toast.error(
        selectedAddress?.id
          ? t('user.update_failed_key')
          : t('user.address_create_failed_key')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const { trigger: submitAddress } = useSWRMutation(
    ['/api/user/addresses', mage],
    ([, mage], { arg: values }) => addUpdateUserAddress(mage, values),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      onError: (error) => toast.error(error.message),
    }
  );

  const renderStepContent = () => {
    switch (addressType) {
      case ADDRESS_MODES.Manual:
        return (
          <AddEditAddressManual
            {...{
              handleSubmit,
              mage,
              t,
              selectedAddress,
              handleOnClose,
            }}
          />
        );
      case ADDRESS_MODES.NATIONAL:
        return (
          <AddEditAddressNationalAddress
            {...{ handleSubmit, selectedAddress, handleOnClose, isLoading }}
          />
        );
      case ADDRESS_MODES.AUTO:
        return (
          <AddEditAddressMap
            {...{ handleSubmit, isLoading, handleOnClose, selectedAddress }}
          />
        );
      default:
        return (
          <AddEditAddressManual
            {...{
              handleSubmit,
              mage,
              t,
              selectedAddress,
            }}
          />
        );
    }
  };

  return <div>{renderStepContent()}</div>;
};

export default AddEditAddressModal;
